import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, FileText, Sparkles, CreditCard } from "lucide-react";

/**
 * Context API
 */
import useModal from "hooks/use-modal";

/**
 * Components
 */
import AccountCard from "blocks/account-card/account-card";
import API from "../../utils/api";
import { MainStore } from "../../stores/Main";

const AccountsPage = () => {
  const mainStore = useContext(MainStore);
  const { openModal, modalTypes, isOpen } = useModal();
  const [accounts, setAccounts] = useState([]);
  const [ready, setReady] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const fetchAccounts = async () => {
    try {
      const response = await API.get("/accounts");
      if (!response.data.result_code) {
        setReady(true);
        setAccounts(response.data.account);
        if (response.data.last_updated) {
          setLastUpdated(response.data.last_updated);
        }
      }
    } catch (e) {
      console.error(e);
      setReady(true);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.1 }}
          className="max-w-lg mx-auto w-full"
        >
          <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-white/10 rounded-lg w-3/4 mb-3" />
              <div className="h-4 bg-white/10 rounded-lg w-1/2 mb-6" />
              <div className="h-10 bg-white/10 rounded-xl mb-4" />
              <div className="h-12 bg-white/10 rounded-2xl" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center py-16"
    >
      <motion.div
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="inline-flex p-6 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 mb-6"
      >
        <CreditCard className="w-16 h-16 text-white/60" />
      </motion.div>
      <h3 className="text-2xl font-semibold text-white mb-3">
        No Accounts Yet
      </h3>
      <p className="text-white/60 mb-8 max-w-md mx-auto">
        Start managing your financial accounts by adding your first account to
        track and resolve.
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => openModal(modalTypes.ModalNewAccount, { fetchAccounts })}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium shadow-lg hover:shadow-xl transition-all"
      >
        <Plus className="w-5 h-5" />
        Add Your First Account
      </motion.button>
    </motion.div>
  );

  return (
    <div className="p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                >
                  <Sparkles className="w-8 h-8 text-yellow-400" />
                </motion.div>
                Your Accounts
              </h2>
              <p className="text-white/60 mt-2">
                Monitor and manage your accounts
              </p>
            </div>
            {/* {ready && accounts.length > 0 && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  openModal(modalTypes.ModalNewAccount, { fetchAccounts })
                }
                className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="w-6 h-6" />
              </motion.button>
            )} */}
          </div>
        </motion.div>

        {/* Account Cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {!ready ? (
            <LoadingSkeleton />
          ) : accounts.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <motion.div
                variants={containerAnimation}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                {accounts.map((account, index) => (
                  <motion.div
                    key={account.id}
                    variants={itemAnimation}
                    className="max-w-lg mx-auto w-full"
                  >
                    <AccountCard account={account} />
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}
        </motion.div>

        {/* Last Updated */}
        {ready && lastUpdated && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center"
          >
            <p className="text-white/40 text-sm">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          </motion.div>
        )}

        {/* Footer Note */}
        {ready && accounts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 p-4 bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl"
          >
            <p className="text-white/50 text-xs text-center">
              All accounts may not be shown. If you believe an account is
              missing or if you have any questions about your accounts, please
              contact our office.
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default AccountsPage;
